import moment from 'moment';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as api from '../../../api';
import * as Device from '../../../utils/device';
import * as PhoneNumber from '../../../utils/phoneNumber';
import * as rootReducer from '../../../reducers/root';
import * as store from '../../../reducers/store';
import { getProviderNameFull } from '../../../utils/provider';
import * as logger from '../../../logger';

import styles from './index.module.css';
import defaultStyles from '../../../styles/formSetStyles.module.css';

import { Button } from '../../../components/Button';
import { StaticStatus } from '../../../components/Question';
import CalendarEvent from '../../../components/CalendarEvent';
import ProviderImage from '../../../components/ProviderImage';
import { getSelectedForms } from '../../../utils/formSet';

interface StateProps {
  forms: store.Forms;
}

interface DispatchProps {
  setFormStatus: (
    formSetId: string,
    formId: string,
    status: api.PrevisitForm.Status,
  ) => void;
}

interface Props extends StateProps, DispatchProps {
  formField: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  encounter: api.Encounter.Encounter;
  provider: api.User.User;
  practice: api.Practice.Practice;
  setFormField: (formFieldId: string, value: string) => void;
  navigateToNextPage: () => Promise<void>;
  handleLastPage: (formSetId: string, formFieldId: string) => Promise<void>;
}

const VirtualVisitPageComponent: React.FC<Props> = (props) => {
  const {
    formField,
    practice,
    setFormField,
    navigateToNextPage,
    setFormStatus,
    forms,
    handleLastPage,
  } = props;
  const { t } = useTranslation();

  useEffect(() => {
    async function maybeUpdateFormField() {
      if (
        formField.value === StaticStatus.Submitted ||
        formField.value === StaticStatus.LastPage
      ) {
        return;
      }
      try {
        if (formField.lastPage) {
          await handleLastPage(formField.previsitFormSetId, formField.id);
        } else {
          await api.client.Previsit.updateFormField(
            formField.previsitFormSetId,
            formField.id,
            StaticStatus.Submitted,
          );
          setFormField(formField.id, StaticStatus.Submitted);
        }
      } catch (err) {
        logger.error(
          err,
          `Failed to update virtual visit instructions ${formField.id} to ${StaticStatus.Submitted}`,
        );
      }
    }
    maybeUpdateFormField();
  }, [formField, setFormField, setFormStatus, forms, handleLastPage]);

  return (
    <div className={styles.Page}>
      <h2 className={defaultStyles.Label}>
        You have an upcoming virtual visit:
      </h2>
      <ProviderCard {...props} />
      <h2 className={defaultStyles.Label}>To prepare for your visit:</h2>
      <ol>
        <li>
          <strong>Download the Zoom App </strong>to your phone by <ZoomLink />.
          Please do this now - installation takes less than a minute.
        </li>
        <li>
          <strong>We'll send you a link </strong>30 minutes before the visit is
          scheduled to start.
        </li>
        <li>
          <strong>Your provider will start the visit. </strong>Please be patient
          as visits may start a few minutes late.
        </li>
        <PhoneNumberListItem practice={practice} />
      </ol>
      {formField.lastPage ? null : (
        <Button type="button" onClick={navigateToNextPage}>
          {t('Continue')}
        </Button>
      )}
    </div>
  );
};

const mapStateToProps = (store: store.FormSetSelected): StateProps => ({
  forms: getSelectedForms(store.formSets, store.selectedFormSetId),
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormStatus: (
      formSetId: string,
      formId: string,
      status: api.PrevisitForm.Status,
    ) => dispatch(rootReducer.setFormStatus({ formSetId, formId, status })),
  };
}

export const VirtualVisitPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(VirtualVisitPageComponent);

export default VirtualVisitPage;

const ProviderCard: React.FC<
  Pick<Props, 'encounter' | 'provider' | 'practice'>
> = ({ provider, encounter, practice }) => {
  const providerName = getProviderNameFull({ provider });
  const description =
    '1. Download the Zoom App to your phone. Please do this now - installation takes less than a minute. \\n\\n' +
    "2. We'll send you a link 30 minutes before the visit scheduled to start. \\n\\n" +
    '3. Your provider will start the visit. Please be patient as visits may start a few minutes late. \\n\\n' +
    (practice.phoneNumber
      ? `4. If you have any questions, contact your practice at ${PhoneNumber.prettify(
          practice.phoneNumber,
        )}`
      : '');
  return (
    <div className={styles.ProviderCard}>
      <div className={styles.ProviderImage}>
        <ProviderImage
          provider={provider}
          size="Small"
          color="Inverse"
          border={false}
        />
      </div>
      <div className={styles.ProviderInfo}>
        <div className={styles.ProviderName}>{providerName}</div>
        <div className={styles.EncounterStartTime}>
          {moment(encounter.startTime).format('LLL')}
        </div>
        {false && (
          <CalendarEvent
            description={description}
            encounter={encounter}
            location={provider.virtualVisitUrl ?? undefined}
            provider={provider}
            practice={practice}
          >
            <div className={[styles.Link, styles.CalendarLink].join(' ')}>
              + Add to Calendar
            </div>
          </CalendarEvent>
        )}
      </div>
    </div>
  );
};

const ZoomLink: React.FC = () => {
  let link: string;
  if (Device.isIOS()) {
    link = 'https://itunes.apple.com/us/app/id546505307';
  } else if (Device.isAndroid()) {
    link =
      'https://play.google.com/store/apps/details?id=us.zoom.videomeetings';
  } else if (Device.isMacOS()) {
    link = 'https://zoom.us/client/latest/zoomusInstaller.pkg';
  } else if (Device.isWindows()) {
    link = 'https://zoom.us/client/latest/ZoomInstaller.exe';
  } else {
    link = 'https://zoom.us/download';
  }
  return (
    <a className={styles.Link} href={link}>
      clicking here
    </a>
  );
};

const PhoneNumberListItem: React.FC<Pick<Props, 'practice'>> = ({
  practice,
}) => {
  const { phoneNumber } = practice;
  if (!phoneNumber) return null;
  const normalizedPhone = PhoneNumber.normalize(phoneNumber);
  const prettyPhone = PhoneNumber.prettify(phoneNumber);
  return (
    <li>
      <strong>If you have any questions</strong>, contact your practice at{' '}
      <a className={styles.Link} href={`tel:${normalizedPhone}`}>
        {prettyPhone}
      </a>
      .
    </li>
  );
};
